/**
 * Ranch → Your data. The "Bring in records" destination from Today: drop in spreadsheets, a
 * CattleMax export, a Hondo V1 download or a backup; settle questions; download everything.
 */
import { useRef, useState, type DragEvent } from "react";
import { Route, Routes, useNavigate, useSearchParams } from "react-router";
import { api } from "../../../web/lib/api";
import { formatDate, timeAgo } from "../../../web/lib/format";
import { plural, t, useLanguage } from "../../../web/lib/i18n";
import { invalidateAll, useApi } from "../../../web/lib/query";
import { useCan } from "../../../web/lib/session";
import { registry } from "../../../web/registry";
import { Button, Card, Disclosure, Empty, ErrorNote, Field, Icon, List, Loading, PageHeader, Row, Section, Select, Tabs, TextInput, toast } from "../../../web/ui/kit";
import { Page } from "../../../web/shell/Layout";
import type { ExportDTO, ImportSummary } from "../shared/types";
import { fileUrl, uploadImport } from "./api";
import { ImportReview } from "./ImportReview";
import { IssueList } from "./Issues";
import { formatBytes, IMPORT_STATUS, StatusChip, sourceName } from "./labels";

export function YourData() {
  return (
    <Routes>
      <Route index element={<DataHome />} />
      <Route path="imports/:id" element={<ImportReview />} />
      <Route path="*" element={<DataHome />} />
    </Routes>
  );
}

function DataHome() {
  useLanguage();
  const can = useCan();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") ?? "imports";
  const imports = useApi<{ items: ImportSummary[] }>(can("data") ? "/m/data/imports" : null);
  const issues = useApi<{ items: unknown[] }>(can("data") ? "/m/data/issues?status=open" : null);
  if (!can("data")) {
    return (
      <Page>
        <PageHeader back={{ to: "/ranch", label: t("Ranch") }} title={t("Your data")} />
        <Empty icon="shield" title={t("Only owners and managers can bring in or download records.")} body={t("Ask them to add your spreadsheet, or tell Hondo what happened and it will file it.")} />
      </Page>
    );
  }
  const open = (imports.data?.items ?? []).filter((i) => ["review", "committing", "failed"].includes(i.status));
  const done = (imports.data?.items ?? []).filter((i) => !["review", "committing", "failed"].includes(i.status));
  return (
    <Page>
      <PageHeader back={{ to: "/ranch", label: t("Ranch") }} title={t("Your data")} subtitle={t("Bring in records, settle what doesn't match, and download everything.")} />
      <Uploader />
      <Tabs value={tab} onChange={(id) => setParams(id === "imports" ? {} : { tab: id }, { replace: true })} tabs={[
        { id: "imports", label: t("In progress"), count: open.length },
        { id: "questions", label: t("Questions"), count: issues.data?.items.length },
        { id: "history", label: t("Past imports") },
        { id: "download", label: t("Download") },
      ]} />
      {tab === "imports" && (imports.isLoading ? <Loading /> : open.length ? <ImportList items={open} /> : <p className="muted">{t("Nothing waiting. Files you bring in show here until they're filed.")}</p>)}
      {tab === "questions" && <IssueList />}
      {tab === "history" && (imports.isLoading ? <Loading /> : done.length ? <ImportList items={done} /> : <p className="muted">{t("No imports yet.")}</p>)}
      {tab === "download" && <Downloads />}
    </Page>
  );
}

function ImportList({ items }: { items: ImportSummary[] }) {
  return (
    <List>
      {items.map((i) => (
        <Row key={i.id} icon="upload" to={`/ranch/data/imports/${i.id}`} title={i.title}
          meta={[t("From {{source}}", { source: sourceName(i.source) }), plural(i.rows, "{{count}} row", "{{count}} rows"), timeAgo(i.created_at), i.open_issues ? plural(i.open_issues, "{{count}} question", "{{count}} questions") : null].filter(Boolean).join(" · ")}
          trailing={<StatusChip map={IMPORT_STATUS} value={i.status} />} />
      ))}
    </List>
  );
}

/** Big, forgiving drop zone. Nothing is filed until a person looks at the preview. */
export function Uploader({ compact }: { compact?: boolean }) {
  const navigate = useNavigate();
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [source, setSource] = useState("");
  const [newSource, setNewSource] = useState("");
  const [exported, setExported] = useState("");
  const sources = useApi<{ items: Array<{ id: string; name: string }> }>("/m/data/sources");
  const send = async (list: FileList | File[] | null) => {
    const files = [...(list ?? [])];
    if (!files.length) return;
    setBusy(true);
    setError(null);
    try {
      const imp = await uploadImport(files, { source_id: source || undefined, source_name: !source && newSource.trim() ? newSource.trim() : undefined, exported_on: exported || undefined });
      await invalidateAll();
      navigate(`/ranch/data/imports/${imp.id}`);
    } catch (err) { setError(err); } finally { setBusy(false); if (input.current) input.current.value = ""; }
  };
  const drop = (e: DragEvent) => { e.preventDefault(); setOver(false); void send(e.dataTransfer.files); };
  return (
    <Card className="data-upload">
      <div className={`data-drop${over ? " is-over" : ""}`} onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={drop}>
        <Icon name="upload" size={32} />
        {!compact && <h2>{t("Bring in records")}</h2>}
        <p>{t("Spreadsheets (CSV or Excel), a CattleMax export, a Hondo V1 download or a Hondo backup. Nothing is filed until you look it over.")}</p>
        <input ref={input} type="file" multiple hidden accept=".csv,.tsv,.txt,.xlsx,.xls,.xlsm,.ods,.zip,.json,.docx,.pdf,.png,.jpg,.jpeg,.webp" onChange={(e) => void send(e.target.files)} aria-label={t("Choose files")} />
        <Button variant="primary" size="lg" icon="upload" busy={busy} onClick={() => input.current?.click()}>{t("Choose files")}</Button>
      </div>
      {error ? <ErrorNote error={error} /> : null}
      <Disclosure label={t("Where is this from?")}>
        <Field label={t("Source")} hint={t("Files from the same system are matched to what came in before, so bringing in a newer copy updates instead of duplicating.")}>
          <Select value={source} onChange={(e) => setSource(e.target.value)}>
            <option value="">{t("Work it out from the file")}</option>
            {(sources.data?.items ?? []).map((s) => <option key={s.id} value={s.id}>{sourceName(s.name)}</option>)}
          </Select>
        </Field>
        {!source && <Field label={t("Or name a new source")} optional><TextInput value={newSource} onChange={(e) => setNewSource(e.target.value)} placeholder={t("Neighbor's sale list")} /></Field>}
        <Field label={t("Exported on")} hint={t("An older file never overwrites newer records. We use the file's date unless you set it.")} optional>
          <TextInput type="date" value={exported} onChange={(e) => setExported(e.target.value)} />
        </Field>
      </Disclosure>
    </Card>
  );
}

const CSV_LISTS: Array<{ kind: string; label: string }> = [
  { kind: "animals", label: "Cattle" }, { kind: "events", label: "Every record" }, { kind: "tasks", label: "Tasks" }, { kind: "places", label: "Places" },
  { kind: "groups", label: "Groups" }, { kind: "memberships", label: "Group membership history" }, { kind: "identifiers", label: "Tags, EIDs and registrations" },
  { kind: "contacts", label: "People and businesses" }, { kind: "equipment", label: "Equipment" }, { kind: "supplies", label: "Supplies" }, { kind: "stock_moves", label: "Supply ins and outs" }, { kind: "jobs", label: "Jobs" },
];

function Downloads() {
  const exports = useApi<{ items: ExportDTO[] }>("/m/data/exports");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const can = useCan();
  const present = useApi<{ items: Array<{ type: string; n: number }> }>("/m/data/export/types");
  const types = (present.data?.items ?? []).map((r) => ({ ...r, def: registry.event(r.type) }));
  const make = async () => {
    setBusy(true);
    setError(null);
    try {
      const e = await api.post<ExportDTO>("/m/data/exports");
      await exports.refetch();
      toast(t("Your download is ready."), { tone: "good" });
      window.location.assign(fileUrl(`/exports/${e.id}/file`));
    } catch (err) { setError(err); } finally { setBusy(false); }
  };
  return (
    <div className="stack">
      <Card>
        <h2>{t("Download everything")}</h2>
        <p className="muted">{can("finance.read")
          ? t("A ZIP with every record as spreadsheets, a backup Hondo can restore, and your attachments. Includes finances.")
          : t("A ZIP with every record as spreadsheets, a backup Hondo can restore, and your attachments. Finances are left out because you can't see them.")}</p>
        <div className="row-actions"><Button variant="primary" size="lg" icon="download" busy={busy} onClick={() => void make()}>{t("Download everything")}</Button></div>
        {error ? <ErrorNote error={error} /> : null}
      </Card>
      {exports.data?.items.length ? (
        <Section title={t("Recent downloads")}>
          <List>{exports.data.items.map((e) => (
            <Row key={e.id} icon="download" title={e.name} meta={[formatDate(e.created_at), formatBytes(e.bytes), e.created_by?.label, e.include_finance ? t("with finances") : t("without finances")].filter(Boolean).join(" · ")}
              trailing={<a className="btn btn-ghost btn-sm" href={fileUrl(`/exports/${e.id}/file`)}>{t("Download")}</a>} />
          ))}</List>
        </Section>
      ) : null}
      <Section title={t("One list at a time")} hint={t("Each list opens in Excel, Numbers or Google Sheets.")}>
        <List>{CSV_LISTS.map((l) => <Row key={l.kind} icon="file" title={t(l.label)} trailing={<a className="btn btn-ghost btn-sm" href={fileUrl(`/export/${l.kind}.csv`)}>{t("Download")}</a>} />)}</List>
        {types.length > 0 && (
          <Disclosure label={t("One kind of record")} count={types.length}>
            <List>{types.map((e) => <Row key={e.type} icon={e.def?.icon ?? "note"} title={e.def ? t(e.def.label) : e.type} meta={plural(e.n, "{{count}} record", "{{count}} records")} trailing={<a className="btn btn-ghost btn-sm" href={fileUrl(`/export/events/${e.type}.csv`)}>{t("Download")}</a>} />)}</List>
          </Disclosure>
        )}
      </Section>
    </div>
  );
}
